import React, { useState, useEffect } from 'react';
import { useGlobalAutoRefreshStore } from '../../stores/useGlobalAutoRefreshStore';
import './GlobalAutoRefreshToggle.css';

const RefreshIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

export const GlobalAutoRefreshToggle: React.FC = () => {
  const isEnabled = useGlobalAutoRefreshStore((state) => state.isEnabled);
  const setEnabled = useGlobalAutoRefreshStore((state) => state.setEnabled);
  const refreshTrigger = useGlobalAutoRefreshStore((state) => state.refreshTrigger);
  const intervalSeconds = useGlobalAutoRefreshStore((state) => state.intervalSeconds);
  const [isSpinning, setIsSpinning] = useState(false);

  // Trigger spin animation when refresh trigger changes
  useEffect(() => {
    if (isEnabled && refreshTrigger > 0) {
      setIsSpinning(true);
      // Stop spinning after animation completes (2 seconds as defined in CSS)
      const timer = setTimeout(() => setIsSpinning(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [refreshTrigger, isEnabled]);

  const handleToggle = () => {
    setEnabled(!isEnabled);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className="global-auto-refresh-toggle-container">
      <button
        className={`toggle-switch ${isEnabled ? 'enabled' : 'disabled'}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        title={isEnabled ? `Auto-refresh enabled (${intervalSeconds}s)` : 'Auto-refresh disabled'}
        aria-label={isEnabled ? 'Disable auto-refresh' : 'Enable auto-refresh'}
        aria-pressed={isEnabled}
        type="button"
        role="switch"
        aria-checked={isEnabled}
      >
        <span className="toggle-track">
          <span className={`toggle-thumb ${isEnabled ? 'enabled' : 'disabled'}`}>
            <RefreshIcon className={isSpinning ? 'spinning' : ''} />
          </span>
        </span>
      </button>
      <span className={`toggle-label ${isEnabled ? 'enabled' : 'disabled'}`}>
        Auto-Refresh{isEnabled ? ` (${intervalSeconds}s)` : ''}
      </span>
    </div>
  );
};

